import { useCallback, useMemo, useState } from 'react';
import {
  Button,
  InlineLoading,
  InlineNotification,
  Search,
  Tag,
} from '@carbon/react';
import {
  CheckmarkFilled,
  Close,
  CloseFilled,
  Rocket,
} from '@carbon/react/icons';
import { useAtom, useSetAtom } from 'jotai';
import { useHistory } from 'react-router-dom';

import { formatResource } from 'utils/misc';
import { formatTcWorkspaceDate } from 'utils/dates';

import {
  tcDetailsRightPanelAtom,
  type TcWorkspacesListAtom,
  type TcWorkspacesListResourceState,
} from './atoms';
import {
  getTcWorkspaceTypeCurveVerification,
  workspacesFromListPayload,
  type TypeCurveWorkspaceResponse,
} from 'types/type-curve-workspace';

import style from './tc-workspaces-list-panel.module.scss';

function normalizeWorkspaces(
  data: TcWorkspacesListResourceState['data']
): TypeCurveWorkspaceResponse[] {
  return workspacesFromListPayload(data);
}

function verificationDisplay(
  status: string | null | undefined
): { label: string; isCorrect: boolean } | null {
  if (!status) {
    return null;
  }
  const normalized = String(status).toLowerCase();
  if (normalized === 'correct') {
    return { label: 'Correct', isCorrect: true };
  }
  if (normalized === 'incorrect') {
    return { label: 'Incorrect', isCorrect: false };
  }
  return null;
}

export interface TcWorkspacesListPanelProps {
  listAtom: TcWorkspacesListAtom;
  /** When set, the shared listing atom is filtered to rows of this `kind` so one fetch feeds the
   *  Type Curve / Inventory / DCA sections. Omit to show every row. */
  kind?: TypeCurveWorkspaceResponse['kind'];
  /** Panel heading. Defaults to "Workspaces" (the Type Curve list). */
  title?: string;
  /** Row-click handler. Defaults to a same-tab push to the Type Curve
   *  workspace; Inventory/DCA lists pass their own (new-tab) opener. */
  onSelect?: (ws: TypeCurveWorkspaceResponse) => void;
  /** False when the collection href isn't available yet (backend hasn't added
   *  it) — the resource atom never resolves, so show the empty state, not a
   *  perpetual spinner. Defaults to true. */
  hasCollection?: boolean;
}

export const TcWorkspacesListPanel = ({
  listAtom,
  kind,
  title = 'Workspaces',
  onSelect,
  hasCollection = true,
}: TcWorkspacesListPanelProps) => {
  const setRightPanel = useSetAtom(tcDetailsRightPanelAtom);
  const history = useHistory();
  const [listState] = useAtom(listAtom);
  const [search, setSearch] = useState('');

  const { loading: rawLoading, error, data } = formatResource(listState);
  // With no collection href the atom is stuck loading; treat it as loaded-empty.
  const loading = hasCollection && rawLoading;
  const workspaces = useMemo(() => {
    const rows = normalizeWorkspaces(
      data as TcWorkspacesListResourceState['data']
    );
    return kind ? rows.filter((w) => w.kind === kind) : rows;
  }, [data, kind]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return workspaces;
    return workspaces.filter(
      (w) =>
        w.name.toLowerCase().includes(q) ||
        (w.createdBy && w.createdBy.toLowerCase().includes(q))
    );
  }, [workspaces, search]);

  const subtitle =
    workspaces.length === 1 ? '1 workspace' : `${workspaces.length} workspaces`;

  const closePanel = useCallback(() => {
    setRightPanel({ type: 'dca', open: false });
    setSearch('');
  }, [setRightPanel]);

  const handleSelect = (ws: TypeCurveWorkspaceResponse) => {
    if (onSelect) {
      onSelect(ws);
      return;
    }
    if (ws.href) {
      history.push(ws.href);
      return;
    }
    const wsId = typeof ws.id === 'number' ? ws.id : Number(ws.id);
    if (!Number.isFinite(wsId)) {
      return;
    }
    history.push(`/type_curve_workspaces/${wsId}`);
  };

  const showRefreshError =
    listState.data &&
    typeof listState.data === 'object' &&
    'error' in listState.data;

  return (
    <div className={style.root}>
      <div className={style.panelHeader}>
        <div>
          <h2 className={style.title}>{title}</h2>
          <p className={style.subtitle}>{subtitle}</p>
        </div>
        <Button
          hasIconOnly
          size="lg"
          kind="ghost"
          onClick={closePanel}
          iconDescription="Close workspaces list"
          tooltipAlignment="center"
          tooltipPosition="bottom"
          renderIcon={(props) => <Close size={20} {...props} />}
        />
      </div>
      <div className={style.panelBody}>
        <div className={style.toolbar}>
          <Search
            labelText=""
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="lg"
          />
        </div>
        {error || showRefreshError ? (
          <InlineNotification
            kind="error"
            title="Could not load workspaces"
            subtitle="Confirm the index response shape with the backend (results or array), or try again later."
            lowContrast
            hideCloseButton
            className={style.errorNote}
          />
        ) : null}
        {loading && workspaces.length === 0 ? (
          <div className={style.loading}>
            <InlineLoading description="Loading workspaces…" />
          </div>
        ) : null}
        {!loading && filtered.length === 0 ? (
          <p className={style.empty}>
            {workspaces.length === 0
              ? 'No workspaces yet. Create one from the header.'
              : 'No workspaces match your search.'}
          </p>
        ) : null}
        <ul className={style.list}>
          {filtered.map((ws) => {
            // The compact `?kinds=` row carries a flat verdict; the rich family row nests it.
            const verification = verificationDisplay(
              ws.verificationStatus ??
                getTcWorkspaceTypeCurveVerification(ws)?.status
            );
            return (
              <li key={ws.id} className={style.card}>
                <button
                  type="button"
                  className={style.cardMain}
                  onClick={() => handleSelect(ws)}
                >
                  <span className={style.cardName}>
                    {ws.name}
                    {/* An inventory list also holds workspaces launched from
                        another type curve whose zones resolve to this one, so
                        the ones launched from here are marked. */}
                    {ws.launchedHere ? (
                      <Tag type="teal" size="sm" className={style.cardTag}>
                        Launched here
                      </Tag>
                    ) : null}
                  </span>
                  <span className={style.cardMeta}>{ws.createdBy}</span>
                  <span className={style.cardDates}>
                    Created: {formatTcWorkspaceDate(ws.createdAt)} | Updated:{' '}
                    {formatTcWorkspaceDate(ws.updatedAt)}
                  </span>
                  {verification || ws.promoted ? (
                    <span className={style.statusRow}>
                      {verification ? (
                        <span
                          className={
                            verification.isCorrect
                              ? style.statusCorrect
                              : style.statusIncorrect
                          }
                        >
                          {verification.isCorrect ? (
                            <CheckmarkFilled size={16} aria-hidden />
                          ) : (
                            <CloseFilled size={16} aria-hidden />
                          )}
                          {verification.label}
                        </span>
                      ) : null}
                      {ws.promoted ? (
                        <span className={style.statusPromoted}>
                          <Rocket size={16} aria-hidden />
                          Promoted
                        </span>
                      ) : null}
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};
